import { useState, useEffect, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Edit, Mail, Calendar, Upload, Loader2, Globe, Users, Eye, Film } from 'lucide-react';
import { toast } from 'sonner';

interface ProfileForm {
  company_name: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  brand_description: string;
  brand_about_us: string;
  brand_social_links: Record<string, string>;
}

const SOCIAL_KEYS = ['twitter', 'instagram', 'youtube', 'tiktok', 'website'] as const;

const Profile = () => {
  const { user, publisher, refreshPublisher } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<ProfileForm>({
    company_name: '',
    contact_email: '',
    contact_phone: '',
    website: '',
    brand_description: '',
    brand_about_us: '',
    brand_social_links: {},
  });

  // Populate form when publisher loads
  useEffect(() => {
    if (publisher) {
      setForm({
        company_name: publisher.company_name || '',
        contact_email: publisher.contact_email || '',
        contact_phone: publisher.contact_phone || '',
        website: publisher.website || '',
        brand_description: publisher.brand_description || '',
        brand_about_us: publisher.brand_about_us || '',
        brand_social_links: (publisher.brand_social_links as Record<string, string>) || {},
      });
    }
  }, [publisher]);

  const handleSave = async () => {
    if (!publisher) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('publishers')
        .update({
          company_name: form.company_name,
          contact_email: form.contact_email,
          contact_phone: form.contact_phone,
          website: form.website,
          brand_description: form.brand_description,
          brand_about_us: form.brand_about_us,
          brand_social_links: form.brand_social_links,
        })
        .eq('id', publisher.id);

      if (error) throw error;
      await refreshPublisher();
      setEditing(false);
      toast.success('Profile updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (
    file: File,
    field: 'brand_logo_url' | 'brand_banner_url',
    setUploading: (v: boolean) => void
  ) => {
    if (!publisher) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `${publisher.id}/profile/${field}-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('publisher-assets')
        .upload(path, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from('publisher-assets').getPublicUrl(path);

      const updates =
        field === 'brand_logo_url'
          ? { brand_logo_url: urlData.publicUrl }
          : { brand_banner_url: urlData.publicUrl };
      const { error: updateError } = await supabase
        .from('publishers')
        .update(updates)
        .eq('id', publisher.id);

      if (updateError) throw updateError;

      await refreshPublisher();
      toast.success(field === 'brand_logo_url' ? 'Logo updated' : 'Banner updated');
    } catch (err: any) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!publisher) {
    return (
      <div className='p-4 md:p-6 space-y-4 md:space-y-6'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold'>Profile</h1>
          <p className='text-muted-foreground text-sm md:text-base'>Manage your partner profile information</p>
        </div>
        <Card className='p-4 md:p-6 partner-card'>
          <div className='flex items-center gap-4'>
            <Skeleton className='h-20 w-20 rounded-full' />
            <div className='space-y-2'>
              <Skeleton className='h-6 w-48' />
              <Skeleton className='h-4 w-32' />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const initials = (publisher.company_name || user?.email || 'P').charAt(0).toUpperCase();
  const joinedDate = publisher.created_at
    ? new Date(publisher.created_at).toLocaleDateString('default', { month: 'long', year: 'numeric' })
    : null;

  return (
    <div className='p-4 md:p-6 space-y-4 md:space-y-6 text-foreground'>
      <div>
        <h1 className='text-2xl md:text-3xl font-bold'>Profile</h1>
        <p className='text-muted-foreground text-sm md:text-base'>
          Manage your partner profile information
        </p>
      </div>

      {/* Banner */}
      <Card className='partner-card overflow-hidden'>
        <div className='relative h-32 md:h-44 bg-muted/30 group'>
          {publisher.brand_banner_url ? (
            <img
              src={publisher.brand_banner_url}
              alt='Banner'
              className='w-full h-full object-cover'
            />
          ) : (
            <div className='w-full h-full flex items-center justify-center text-muted-foreground/40 text-sm'>
              No banner image
            </div>
          )}
          <button
            className='absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity'
            onClick={() => bannerInputRef.current?.click()}
            disabled={uploadingBanner}
          >
            {uploadingBanner ? (
              <Loader2 className='h-6 w-6 text-white animate-spin' />
            ) : (
              <div className='flex items-center gap-2 text-white text-sm font-medium'>
                <Upload className='h-4 w-4' />
                Change Banner
              </div>
            )}
          </button>
          <input
            ref={bannerInputRef}
            type='file'
            accept='image/*'
            className='hidden'
            onChange={e => {
              const f = e.target.files?.[0];
              if (f) handleImageUpload(f, 'brand_banner_url', setUploadingBanner);
            }}
          />
        </div>

        {/* Avatar + Identity row */}
        <div className='px-4 md:px-6 pb-4 md:pb-6'>
          <div className='flex flex-col sm:flex-row sm:items-end gap-4 -mt-10 sm:-mt-12'>
            <div className='relative group w-fit'>
              <Avatar className='h-20 w-20 md:h-24 md:w-24 border-4 border-background bg-primary/15 text-primary'>
                {publisher.brand_logo_url ? (
                  <AvatarImage src={publisher.brand_logo_url} alt='Logo' />
                ) : null}
                <AvatarFallback className='text-2xl font-bold bg-primary/15 text-primary'>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <button
                className='absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity'
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <Loader2 className='h-5 w-5 text-white animate-spin' />
                ) : (
                  <Upload className='h-5 w-5 text-white' />
                )}
              </button>
              <input
                ref={logoInputRef}
                type='file'
                accept='image/*'
                className='hidden'
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) handleImageUpload(f, 'brand_logo_url', setUploadingLogo);
                }}
              />
            </div>

            <div className='flex-1 min-w-0 pb-1'>
              <h2 className='text-lg md:text-xl font-semibold truncate'>
                {publisher.company_name || user?.email}
              </h2>
              <div className='flex flex-wrap items-center gap-x-4 gap-y-1 mt-1'>
                {publisher.contact_email && (
                  <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                    <Mail className='h-3 w-3' />
                    {publisher.contact_email}
                  </span>
                )}
                {joinedDate && (
                  <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                    <Calendar className='h-3 w-3' />
                    Joined {joinedDate}
                  </span>
                )}
              </div>
            </div>

            <Button
              variant='outline'
              size='sm'
              className='border-border/60 hover:bg-muted/60 sm:self-end'
              onClick={() => setEditing(e => !e)}
            >
              <Edit className='h-4 w-4 mr-2' />
              {editing ? 'Cancel' : 'Edit Profile'}
            </Button>
          </div>
        </div>
      </Card>

      {/* Stats row */}
      <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
        {[
          { icon: Users, label: 'Followers', value: publisher.follower_count ?? 0 },
          { icon: Film, label: 'Works', value: publisher.content_count ?? 0 },
          { icon: Eye, label: 'Total Views', value: publisher.total_views ?? 0 },
          { icon: Upload, label: 'Uploads', value: publisher.total_uploads ?? 0 },
        ].map(({ icon: Icon, label, value }) => (
          <Card key={label} className='p-3 md:p-4 partner-card text-center'>
            <Icon className='h-4 w-4 text-muted-foreground mx-auto mb-1' />
            <p className='text-lg font-bold'>{Number(value).toLocaleString()}</p>
            <p className='text-xs text-muted-foreground'>{label}</p>
          </Card>
        ))}
      </div>

      {/* Status badges */}
      <Card className='p-4 md:p-6 partner-card'>
        <h3 className='text-sm font-medium text-muted-foreground mb-3'>Account Status</h3>
        <div className='flex flex-wrap gap-2'>
          <Badge variant='outline' className='capitalize'>
            {publisher.partnership_tier || 'basic'} tier
          </Badge>
          <Badge
            variant='outline'
            className={
              publisher.status === 'approved'
                ? 'border-green-500/40 text-green-500'
                : publisher.status === 'pending_review'
                ? 'border-amber-500/40 text-amber-500'
                : 'border-destructive/40 text-destructive'
            }
          >
            {publisher.status?.replace('_', ' ') || 'unknown'}
          </Badge>
          {(publisher.contract_status || publisher.contract_signed != null) && (
            <Badge
              variant='outline'
              className={
                publisher.contract_signed
                  ? 'border-green-500/40 text-green-500 capitalize'
                  : 'border-amber-500/40 text-amber-500 capitalize'
              }
            >
              Contract: {publisher.contract_signed ? 'signed' : (publisher.contract_status?.replace('_', ' ') ?? 'pending')}
            </Badge>
          )}
          {publisher.is_verified && (
            <Badge className='bg-primary/10 text-primary border-primary/30'>Verified</Badge>
          )}
        </div>
      </Card>

      {/* Edit form */}
      {editing && (
        <Card className='p-4 md:p-6 partner-card'>
          <h3 className='text-base md:text-lg font-semibold mb-4'>Edit Profile</h3>
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div className='space-y-1.5'>
              <Label htmlFor='company_name'>Company Name</Label>
              <Input
                id='company_name'
                value={form.company_name}
                onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                disabled={saving}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='contact_email'>Contact Email</Label>
              <Input
                id='contact_email'
                type='email'
                value={form.contact_email}
                onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
                disabled={saving}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='contact_phone'>Phone</Label>
              <Input
                id='contact_phone'
                value={form.contact_phone}
                onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))}
                disabled={saving}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='website'>Website</Label>
              <Input
                id='website'
                value={form.website}
                onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
                placeholder='https://yourstudio.com'
                disabled={saving}
              />
            </div>
            <div className='space-y-1.5 sm:col-span-2'>
              <Label htmlFor='brand_description'>Brand Description</Label>
              <Input
                id='brand_description'
                value={form.brand_description}
                onChange={e => setForm(f => ({ ...f, brand_description: e.target.value }))}
                placeholder='Short public tagline for your studio'
                disabled={saving}
              />
            </div>
            <div className='space-y-1.5 sm:col-span-2'>
              <Label htmlFor='brand_about_us'>About Us</Label>
              <Input
                id='brand_about_us'
                value={form.brand_about_us}
                onChange={e => setForm(f => ({ ...f, brand_about_us: e.target.value }))}
                placeholder='Longer description shown on your public studio page'
                disabled={saving}
              />
            </div>

            <Separator className='sm:col-span-2' />

            <div className='sm:col-span-2'>
              <p className='text-sm font-medium mb-3 flex items-center gap-2'>
                <Globe className='h-4 w-4' />
                Social Links
              </p>
              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
                {SOCIAL_KEYS.map(key => (
                  <div key={key} className='space-y-1.5'>
                    <Label htmlFor={`social_${key}`} className='capitalize'>{key}</Label>
                    <Input
                      id={`social_${key}`}
                      value={form.brand_social_links[key] || ''}
                      onChange={e =>
                        setForm(f => ({
                          ...f,
                          brand_social_links: { ...f.brand_social_links, [key]: e.target.value },
                        }))
                      }
                      placeholder={`https://${key}.com/yourstudio`}
                      disabled={saving}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className='flex gap-2 mt-5'>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              Save Changes
            </Button>
            <Button variant='ghost' onClick={() => setEditing(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {/* Read-only brand info (when not editing) */}
      {!editing && (publisher.brand_description || publisher.brand_about_us || publisher.brand_social_links) && (
        <Card className='p-4 md:p-6 partner-card'>
          <h3 className='text-base md:text-lg font-semibold mb-4'>Brand Information</h3>
          <div className='space-y-4'>
            {publisher.brand_description && (
              <div>
                <p className='text-xs font-medium text-muted-foreground mb-1'>Description</p>
                <p className='text-sm'>{publisher.brand_description}</p>
              </div>
            )}
            {publisher.brand_about_us && (
              <div>
                <p className='text-xs font-medium text-muted-foreground mb-1'>About Us</p>
                <p className='text-sm'>{publisher.brand_about_us}</p>
              </div>
            )}
            {publisher.brand_social_links && Object.keys(publisher.brand_social_links).length > 0 && (
              <div>
                <p className='text-xs font-medium text-muted-foreground mb-2'>Social Links</p>
                <div className='flex flex-wrap gap-2'>
                  {Object.entries(publisher.brand_social_links as Record<string, string>)
                    .filter(([, url]) => url)
                    .map(([platform, url]) => (
                      <a
                        key={platform}
                        href={url}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-xs text-primary hover:underline capitalize flex items-center gap-1'
                      >
                        <Globe className='h-3 w-3' />
                        {platform}
                      </a>
                    ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default Profile;
